/** SemVer precedence for stable and prerelease application updates. */
export interface PublicReleaseInfo {
  version: string
  buildId?: string
}

const SEMVER = /^v?(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([\da-zA-Z-]+(?:\.[\da-zA-Z-]+)*))?(?:\+[\da-zA-Z-]+(?:\.[\da-zA-Z-]+)*)?$/
const NUMERIC_IDENTIFIER = /^\d+$/

function parseVersion(version: string) {
  const match = SEMVER.exec(version.trim())
  if (!match) return null
  const prerelease = match[4]?.split('.') ?? []
  if (prerelease.some((part) => NUMERIC_IDENTIFIER.test(part) && part.length > 1 && part.startsWith('0'))) return null
  return { core: match.slice(1, 4).map(BigInt), prerelease }
}

/** Build metadata does not affect precedence; numeric beta identifiers compare numerically. */
export function isSemverGreater(candidate: string, current: string): boolean {
  const next = parseVersion(candidate)
  const previous = parseVersion(current)
  if (!next || !previous) return false

  for (let index = 0; index < next.core.length; index++) {
    if (next.core[index] !== previous.core[index]) return next.core[index] > previous.core[index]
  }
  if (!next.prerelease.length || !previous.prerelease.length) {
    return !next.prerelease.length && previous.prerelease.length > 0
  }
  for (let index = 0; index < Math.max(next.prerelease.length, previous.prerelease.length); index++) {
    const left = next.prerelease[index]
    const right = previous.prerelease[index]
    if (left === right) continue
    if (left === undefined) return false
    if (right === undefined) return true
    const leftNumeric = NUMERIC_IDENTIFIER.test(left)
    const rightNumeric = NUMERIC_IDENTIFIER.test(right)
    if (leftNumeric && rightNumeric) return BigInt(left) > BigInt(right)
    if (leftNumeric !== rightNumeric) return !leftNumeric
    return left > right
  }
  return false
}
